import { Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { RequireRole } from "./components/RequireRole";
import { EmptyState } from "./components/EmptyState";
import { useAuth } from "./lib/auth";

import { Login } from "./pages/Login";
import { Setup } from "./pages/Setup";
import { Dashboard } from "./pages/Dashboard";
import { Orders } from "./pages/Orders";
import { OrderDetail } from "./pages/OrderDetail";
import { CancelledOrders } from "./pages/CancelledOrders";
import ManualOrder from "./pages/ManualOrder";
import { Production } from "./pages/Production";
import { ProductionDetail } from "./pages/ProductionDetail";
import { CuttingList } from "./pages/CuttingList";
import { LegList } from "./pages/LegList";
import AfterSales from "./pages/AfterSales";
import StockOrders from "./pages/StockOrders";
import Stock from "./pages/Stock";
import StockMovements from "./pages/StockMovements";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import RecipePage from "./pages/Recipe";
import PackagingPage from "./pages/Packaging";
import { Components } from "./pages/Components";
import Procurement from "./pages/Procurement";
import Reports from "./pages/Reports";
import ProductionReport from "./pages/ProductionReport";
import Finance from "./pages/Finance";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import AuditLog from "./pages/AuditLog";
import { Notifications } from "./pages/Notifications";
import { Search } from "./pages/Search";
import { Shipping } from "./pages/Shipping";

/**
 * Rota tablosu — menüdeki HER bağlantının bir karşılığı vardır.
 *
 * Yol adları İngilizce, arayüz metinleri Türkçedir: rota adı sözleşmedir,
 * modüller arasında tahmin edilebilir olmalıdır. Yollar `Sidebar.tsx`
 * içindeki `to:` değerleriyle birebir eşleşir — eşleşmeyen bağlantı
 * kullanıcıyı boş ekrana götürür ve bu sessiz bir hatadır.
 *
 * Rol kısıtı BURADA yalnızca kullanılabilirlik içindir; gerçek koruma her
 * zaman Security Rules ve Cloud Function guard'larındadır.
 */

function NotFound() {
  const location = useLocation();
  return (
    <EmptyState
      title="Sayfa bulunamadı"
      description={`"${location.pathname}" diye bir ekran yok. Menüden devam edebilirsiniz.`}
      tone="warning"
    />
  );
}

export function App() {
  const { needsSetup } = useAuth();

  // Oturum açık ama rol atanmamış → yalnızca kurulum ekranı.
  // Sistemde ilk iş sahibi oluşana kadar geçerlidir; sonrasında rolsüz
  // kullanıcı zaten oluşmaz (hesaplar rolüyle birlikte açılır).
  if (needsSetup) {
    return (
      <Routes>
        <Route path="*" element={<Setup />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />

        {/* ── SİPARİŞ ───────────────────────────────────────────────────── */}
        {/* Sıra önemli: /orders/cancelled, /orders/:orderId'den ÖNCE gelmeli. */}
        <Route path="orders" element={<Orders />} />
        <Route
          path="orders/cancelled"
          element={
            <RequireRole roles={["boss", "office"]}>
              <CancelledOrders />
            </RequireRole>
          }
        />
        {/* Manuel sipariş (çekim/numune/iç kullanım) — üretim rolü GÖREMEZ.
            Sıra önemli: "manual" sabiti :orderId'den ÖNCE gelmeli, yoksa
            ekran açılmaz ve "man" kimlikli sipariş aranmaya çalışılır. */}
        <Route
          path="orders/manual"
          element={
            <RequireRole roles={["boss", "office"]}>
              <ManualOrder />
            </RequireRole>
          }
        />
        <Route path="orders/:orderId" element={<OrderDetail />} />

        {/* ── ÜRETİM ────────────────────────────────────────────────────── */}
        <Route path="production" element={<Production />} />
        <Route path="production/:productionId" element={<ProductionDetail />} />
        <Route path="cutting-list" element={<CuttingList />} />
        <Route path="leg-list" element={<LegList />} />
        <Route path="after-sales" element={<AfterSales />} />

        {/* ── STOK ──────────────────────────────────────────────────────── */}
        <Route path="stock-orders" element={<StockOrders />} />
        <Route path="inventory" element={<Stock />} />
        <Route path="inventory/movements" element={<StockMovements />} />

        {/* ── ÜRÜN VE KATALOG ───────────────────────────────────────────── */}
        <Route path="products" element={<Products />} />
        {/*
          ★ AYRI BİR "products/new" ROTASI YOKTUR — VE OLMAMALIDIR.

          Vardı ve "Ürün Ekle" düğmesini SESSİZCE KIRIYORDU. Sebep: statik
          rotada `:productId` diye bir segment bulunmadığı için `useParams()`
          BOŞ döner. `ProductDetail` ise kimliği oradan okuyup `"new"` ile
          karşılaştırıyor (`ProductDetail.tsx:107,120`); boş dize `"new"`e eşit
          olmadığı için "yeni ürün" dalı hiç çalışmıyor, ekran var olmayan bir
          dokümana — `products/` — abone olmaya çalışıyordu. Firestore bu tek
          segmentli yolu istisna ile reddeder: ürün ekleme ekranı açılmıyordu ve
          `NewProduct` bileşeni tamamen ölü koddu.

          Tek dinamik rota bırakıldığında `/products/new` bu rotayla eşleşir,
          `productId === "new"` olur ve bileşenin KENDİ mantığı doğru çalışır.
          Aynı desenin `orders/manual` hâlinde sorun çıkarmamasının sebebi,
          oranın AYRI bir bileşene (`ManualOrder`) gitmesi ve rota parametresi
          okumamasıdır.
        */}
        <Route path="products/:productId" element={<ProductDetail />} />
        <Route path="products/:productId/recipe" element={<RecipePage />} />
        <Route path="products/:productId/packaging" element={<PackagingPage />} />

        {/* Parça yönetimi ve satın alma: üretim rolü GÖREMEZ
            (eski koli_yonetimi.php ve procurement.php ile aynı kural). */}
        <Route
          path="components"
          element={
            <RequireRole roles={["boss", "office"]}>
              <Components />
            </RequireRole>
          }
        />
        <Route
          path="procurement"
          element={
            <RequireRole roles={["boss", "office"]}>
              <Procurement />
            </RequireRole>
          }
        />

        {/* ── KARGO VE BİLDİRİM ─────────────────────────────────────────── */}
        <Route
          path="shipping"
          element={
            <RequireRole roles={["boss", "office"]}>
              <Shipping />
            </RequireRole>
          }
        />
        <Route path="notifications" element={<Notifications />} />
        {/* Üst çubuktaki arama kutusu buraya gelir; rota yokken her arama
            "Sayfa bulunamadı" ekranına düşüyordu. */}
        <Route path="search" element={<Search />} />

        {/* ── RAPORLAR — kârlılık: boss VE financeView ─────────────────────
            ★ KAPI ARTIK SUNUCUYLA BİREBİR AYNI KOŞULU SORUYOR.

            Uç noktalar İKİ koşulu birden arıyor
            (`reports/profit.ts:124-126` → `assertRole(caller,["boss"])` +
            `assertFinanceView(caller)`). Ekran kapısı yalnızca rolü
            soruyordu, menü ise (`Sidebar.tsx`) yalnızca `financeView`
            claim'ini — üç yerde ÜÇ farklı koşul vardı.

            Sonucu: iş sahibi bir ofis kullanıcısına `financeView` verdiği anda
            (Users ekranı bunu açıkça sunuyor) menüde "Kârlılık Tablosu"
            beliriyor, tıklayınca "erişim yetkiniz yok" paneline düşüyordu.
            Ölçüm: bugün canlıda `financeView` yalnızca iki boss hesabında true,
            yani sapma henüz tetiklenmemişti — yetki verildiği gün patlardı.

            `RequireRole` `financeView` desteğini zaten taşıyordu, hiç
            kullanılmamıştı. Otorite SUNUCUDUR; arayüz ona uydurulur.
        */}
        {/* Üretim raporu: para/maliyet içermez — üç rol de görür
            (sunucu: `reports/productionReport.ts` → `assertRole(caller, ALL_ROLES)`). */}
        <Route path="reports/production" element={<ProductionReport />} />
        <Route
          path="reports/profit"
          element={
            <RequireRole roles={["boss"]} financeView>
              <Reports />
            </RequireRole>
          }
        />
        <Route
          path="reports/solid-panel"
          element={
            <RequireRole roles={["boss"]} financeView>
              <Reports />
            </RequireRole>
          }
        />

        {/* ── FİNANS — yalnızca iş sahibi ────────────────────────────────
            Ciro, satılan ürünlerin malzeme maliyeti, kesilecek fatura tutarı,
            reklam/personel/genel/SSH/özel giderler ve kârlılık özeti.

            ★ KAPI SUNUCUYLA BİREBİR AYNI KOŞULU SORUYOR: uç noktalar
            `assertRole(caller,["boss"])` + `assertFinanceView(caller)` arıyor
            (`functions/src/finance/dashboard.ts`), menü de aynı iki koşula
            bakıyor (`Sidebar.tsx`). Üç yerde üç farklı koşul olması,
            `reports/profit` yolunda "menüde görünüyor ama tıklayınca 403"
            hatasını doğurmuştu; aynı sapma burada baştan kapatıldı.

            Arayüz kapısı yalnızca KULLANILABİLİRLİK içindir. Gerçek koruma
            callable guard'ında ve `firestore.rules`'tadır — yalnızca ekranda
            gizlemek eski sistemin G-08 açığıydı. */}
        <Route
          path="finance"
          element={
            <RequireRole roles={["boss"]} financeView>
              <Finance />
            </RequireRole>
          }
        />

        {/* ── YÖNETİM — yalnızca iş sahibi ───────────────────────────────── */}
        <Route
          path="users"
          element={
            <RequireRole roles={["boss"]}>
              <Users />
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole roles={["boss"]}>
              <Settings />
            </RequireRole>
          }
        />
        <Route
          path="audit-log"
          element={
            <RequireRole roles={["boss"]}>
              <AuditLog />
            </RequireRole>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
